import type { Field } from 'components/user/DummyHelios/Uploader/UploadFile';
import { ReactNativeMessageType } from 'types';
import { invokeRNMessage, isMobileApp } from 'utils/common';
import { checkIfFileNameHasExtension, getDownloadFileName } from './fileUtils';

// #region CSV Download

const downloadCsv = (csvContent: string, filename: string) => {
  if (isMobileApp()) {
    const file = btoa(Array.from(new TextEncoder().encode(csvContent), (byte) => String.fromCharCode(byte)).join(''));

    invokeRNMessage(ReactNativeMessageType.DownloadFileBase64, {
      fileName: filename,
      fileType: 'csv',
      file,
    });

    return;
  }

  const url = URL.createObjectURL(new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8' }));

  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    link.click();
  } finally {
    URL.revokeObjectURL(url);
  }
};

// #endregion CSV Download
// #region Base64 Download

const validateBase64 = (base64: string) => {
  if (!base64 || typeof base64 !== 'string') {
    throw new Error('Invalid file content: must be a non-empty string');
  }

  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
  if (!base64Regex.test(base64)) {
    throw new Error('Invalid base64 format');
  }
};

const base64ToBlob = (base64: string, mimeType?: string): Blob => {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));

  return new Blob([bytes], mimeType ? { type: mimeType } : undefined);
};

const downloadBase64File = ({
  base64,
  fileName,
  extension,
  mimeType,
}: {
  base64: string;
  fileName: string;
  extension: string;
  mimeType?: string;
}) => {
  validateBase64(base64);

  if (isMobileApp()) {
    invokeRNMessage(ReactNativeMessageType.DownloadFileBase64, {
      fileName,
      fileType: extension,
      file: base64,
    });

    return;
  }

  const blob = base64ToBlob(base64, mimeType);
  const url = URL.createObjectURL(blob);

  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = checkIfFileNameHasExtension(fileName) || !extension ? fileName : `${fileName}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(url);
  }
};

// #endregion Base64 Download
// #region Helios File Download

const downloadHeliosFile = async ({ file, url, extension, token }: { file: Field; url: string; extension: string; token: string }) => {
  if (isMobileApp()) {
    invokeRNMessage(ReactNativeMessageType.DownloadFile, { file: { ...file, url, extension }, token: token });
    return;
  }

  const response = await fetch(url, {
    headers: {
      Authorization: token,
    },
  });

  if (!response.ok) throw new Error('Failed to download file');

  const blob = await response.blob();

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = getDownloadFileName(file.name, extension);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

// #endregion Helios File Download

export { downloadBase64File, downloadCsv, downloadHeliosFile };
